/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useLocalStorage, useSessionStorage } from "./useStorage";
import { useCallback, useMemo } from "react";
import { axiosConfig, updateAxiosHeaders } from "../axios.init";
import { getClientSessionTokenKey } from "../constants/constants";
import useWidgetCallbackParams from "./useWidgetCallbackParams";
import { queryKeys } from "../lib/react-query/queryKeys";

export const useTokenStorage = () => {
  const queryClient = useQueryClient();
  const storageKey = getClientSessionTokenKey(axiosConfig.companyLogin);
  const [sessionToken, setSessionToken] = useSessionStorage(storageKey, null);
  const [localToken, setLocalToken] = useLocalStorage(storageKey, null);
  const { token: urlToken } = useWidgetCallbackParams();

  const setToken = useCallback(
    ({ token, rememberMe, isAnonymous }) => {
      if (token) {
        updateAxiosHeaders({ "X-Token": token });
        queryClient.setQueryData(queryKeys.token.all, token);
      }

      if (!isAnonymous) {
        rememberMe ? setLocalToken(token) : setSessionToken(token);
      }
    },
    [setLocalToken, setSessionToken, queryClient]
  );

  const clearTokens = useCallback(() => {
    setLocalToken(null);
    setSessionToken(null);
    updateAxiosHeaders({ "X-Token": null });
  }, [setLocalToken, setSessionToken]);

  // Token resolution with priority: URL > localStorage > sessionStorage
  const token = urlToken || localToken || sessionToken;
  const rememberMe = !!localToken;
  const source = urlToken ? "url" : localToken ? "localStorage" : sessionToken ? "sessionStorage" : null;

  return useMemo(() => ({ 
    setToken, 
    clearTokens, 
    token, 
    rememberMe,
    source
  }), [setToken, clearTokens, token, rememberMe, source]);
};
